import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Calendar, dateFnsLocalizer, Navigate } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import endOfWeek from 'date-fns/endOfWeek';
import startOfMonth from 'date-fns/startOfMonth';
import endOfMonth from 'date-fns/endOfMonth';
import getDay from 'date-fns/getDay';
import addDays from 'date-fns/addDays'; // 用來加日
import subDays from 'date-fns/subDays'; // 用來減日
import zhTW from 'date-fns/locale/zh-TW';
import 'react-big-calendar/lib/css/react-big-calendar.css';

// 引入 API (請確保 api.js 有 export getMyRoster)
import { getMyRoster, getAllRoster } from '../../services/api';

// 1. 設定 Localizer
const locales = { 'zh-TW': zhTW };
const localizer = dateFnsLocalizer({
  format, parse, startOfWeek, getDay, locales,
});

// 2. 設定時間範圍
const minTime = new Date();
minTime.setHours(10, 0, 0);
const maxTime = new Date();
maxTime.setHours(23, 30, 0);

// ==========================================
// 🎨 自訂 Event 顯示 (簡化版，因為係自己睇)
// ==========================================
const CustomEvent = ({ event }) => {
  return (
    <div className="h-full flex flex-col justify-center leading-none px-1">
      {/* 這裡最重要：直接用 Date 物件 format，保證跟方塊長度百分之百同步 */}
      <div className="text-[10px] font-bold opacity-90 mb-0.5">
        {format(event.start, 'HH:mm')} - {format(event.end, 'HH:mm')}
      </div>
      
      <div className="text-xs font-normal leading-tight whitespace-normal break-words">
        {/* 只顯示人名或標題，不要重複顯示時間 */}
        {event.staffName && (
            <span className="font-bold text-yellow-200 mr-1">{event.staffName}</span>
        )}
        {/* 這裡解析標題，去掉標題裡面可能帶有的舊時間字串 */}
        <span>{event.title.split(' ')[0]}</span> 
      </div>
    </div>
  );
};

// ==========================================
// 🛠️ 自訂 Toolbar (瘦身版 - 唯讀)
// ==========================================
const IntegratedToolbar = ({ onNavigate, label, view, onView, onRefresh, viewMode, setViewMode }) => {
    const goToBack = () => onNavigate(Navigate.PREVIOUS);
    const goToNext = () => onNavigate(Navigate.NEXT);
    const goToToday = () => onNavigate(Navigate.TODAY);

    const handleViewChange = (e) => {
        onView(e.target.value);
        onNavigate(Navigate.TODAY);
    };

    return (
      <div className="flex items-center justify-between p-1 mb-1 bg-gray-50 border-b border-gray-200 gap-2">
        
        {/* 左邊：導航區 */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="relative">
              <select
                  value={view}
                  onChange={handleViewChange}
                  className="appearance-none pl-2 pr-6 py-1 bg-indigo-50 border border-indigo-200 rounded-md text-xs font-bold text-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer shadow-sm h-8" 
              >
                  <option value="month">📅 月</option>
                  <option value="week">📅 週</option>
                  <option value="day">📅 日</option>
              </select>
          </div>

          <div className="flex items-center bg-white border border-gray-300 rounded-lg shadow-sm overflow-hidden h-8">
             <button onClick={goToBack} className="px-3 text-gray-500 hover:text-indigo-600 hover:bg-gray-50 border-r border-gray-200 transition-colors h-full flex items-center justify-center">
               <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path></svg>
             </button>
             {/*<span className="text-sm font-bold text-gray-800 px-4 whitespace-nowrap text-center min-w-[3.5rem] flex items-center justify-center h-full">
                {label.replace('2025年', '')}
             </span>
             */}
             <button onClick={goToNext} className="px-3 text-gray-500 hover:text-indigo-600 hover:bg-gray-50 border-l border-gray-200 transition-colors h-full flex items-center justify-center">
               <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path></svg>
             </button>
          </div>
          
          {/*<button onClick={goToToday} className="ml-1 px-3 h-8 flex items-center justify-center text-xs font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded border border-gray-300 transition">
            今日
          </button>
          */}
        </div>

        {/* 右邊：Refresh */}
        <div className="flex items-center gap-2 flex-1 justify-end min-w-0">
          {/* 👇 切換 Own / All 的 Dropdown */}
          <div className="relative">
               <select
                   value={viewMode}
                   onChange={(e) => setViewMode(e.target.value)}
                   className="appearance-none pl-3 pr-8 py-1 bg-white border border-gray-300 rounded-md text-xs font-bold text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 cursor-pointer shadow-sm h-8"
               >
                   <option value="self">👤自己</option>
                   <option value="all">👥全部</option>
               </select>
               {/* 下箭頭 Icon */}
               <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
               </div>
          </div>
          <button 
              onClick={onRefresh}
              className="p-1.5 bg-white border border-gray-300 text-gray-700 rounded shadow-sm hover:bg-gray-50 active:bg-gray-100 flex-shrink-0 h-8 w-8 flex items-center justify-center"
              title="重新整理"
          >
              <span className="text-sm">🔄</span>
          </button>
        </div>
      </div>
    );
};

// ==========================================
// 🌟 主組件
// ==========================================
const StaffRosterView = () => {
  const [events, setEvents] = useState([]);
  const [date, setDate] = useState(new Date());
  const [view, setView] = useState('week');

  // 🌟 State: 控制睇自己定全部 ('self' | 'all')
  const [viewMode, setViewMode] = useState('self');
  const [loadedRange, setLoadedRange] = useState({ start: null, end: null });
  
  const loadedRangeRef = useRef({ start: null, end: null });

  // 1. 當 ViewMode 改變時，清空緩存，因為資料內容唔同左
  useEffect(() => {
      setEvents([]); 
      //setLoadedRange({ start: null, end: null }); 
      loadedRangeRef.current = { start: null, end: null }; // 修改 Ref
  }, [viewMode]);

  // 2. 智慧下載邏輯 (Buffer Loading)
  const fetchRoster = useCallback(async (targetDate, targetView, forceRefresh = false) => {
    try {
      // A. 計算當前視圖的可視範圍
      let visibleStart, visibleEnd;
      const current = targetDate || new Date(); // 防呆

      if (targetView === 'month') {
          visibleStart = startOfWeek(startOfMonth(current));
          visibleEnd = endOfWeek(endOfMonth(current));
      } else { 
          visibleStart = startOfWeek(current);
          visibleEnd = endOfWeek(current);
      }

      const loadedRange = loadedRangeRef.current; // 讀取 Ref

      // B. 檢查緩存 (Cache Check)
      if (!forceRefresh && loadedRange.start && loadedRange.end) {
          // 如果想看的範圍完全在已下載範圍內 -> Skip
          if (visibleStart >= loadedRange.start && visibleEnd <= loadedRange.end) {
              //console.log("🟢 Cache Hit");
              return;
          }
      }

      // C. 需要下載：前後緩衝 30 天
      const apiStart = subDays(visibleStart, 30); 
      const apiEnd = addDays(visibleEnd, 30);
      
      const params = {
          start: apiStart.toISOString(),
          end: apiEnd.toISOString()
      };

      let response;
      // 🌟 根據模式切換 API
      if (viewMode === 'all') {
          response = await getAllRoster(params);
      } else {
          response = await getMyRoster(params);
      }
      
      const loadedEvents = response.data.results.map(evt => ({
        id: evt.id,
        title: evt.title, 
        start: new Date(evt.start),
        end: new Date(evt.end),
        resourceId: evt.resourceId,
        staffName: evt.staff_name, // 存入人名
        type: evt.type, // 'shift'
        color: evt.staff_color || '#059669'
      }));

      setEvents(loadedEvents);

      loadedRangeRef.current = { start: apiStart, end: apiEnd };
    } catch (err) {
      console.error("Load roster failed:", err);
    }
  }, [viewMode]); // 🌟 當 date 或 viewMode 改變時，重新下載

  useEffect(() => {
    fetchRoster(date, view);
  }, [date, view, viewMode, fetchRoster]);

  const onNavigate = useCallback((newDate) => setDate(newDate), []);
  const onView = useCallback((newView) => setView(newView), []);
  const onRefresh = useCallback(() => fetchRoster(date, view, true), [fetchRoster, date, view]);

  // 2. 自訂格式
  const formats = useMemo(() => ({
      monthHeaderFormat: (date, culture, localizer) => localizer.format(date, 'yyyy年 MM月', culture),
      dayRangeHeaderFormat: ({ start, end }, culture, localizer) =>
        `${localizer.format(start, 'MM/dd', culture)} - ${localizer.format(end, 'MM/dd', culture)}`,
      dayFormat: (date, culture, localizer) => localizer.format(date, 'MM/dd (eeeee)', culture),
      eventTimeRangeFormat: () => "",
      timeGutterFormat: (date, culture, localizer) => localizer.format(date, 'HH:mm', culture),
      dateFormat: (date, culture, localizer) => localizer.format(date, 'd', culture),
  }), []);

  // 3. Event 樣式 (綠色：代表正式/已確認)
  const eventStyleGetter = (event) => {
    return {
      style: {
        backgroundColor: event.color,
        borderRadius: '6px',
        opacity: 0.9,
        border: '1px solid rgba(255,255,255,0.2)',
        color: 'white',
        display: 'block',
        boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
      }
    };
  };

  return (
    <div className="flex flex-col h-full"> 
      <div className="flex-grow relative" style={{ minHeight: '0', touchAction: 'none'}}>
        <Calendar
          className="locked-roster-view" // 套用 CSS
          date={date}
          view={view}
          onNavigate={onNavigate}
          onView={onView}
          
          dayLayoutAlgorithm='no-overlap' 
          culture='zh-TW'
          
          components={{
            // 傳入 viewMode 和 setViewMode 給 Toolbar
            toolbar: (props) => <IntegratedToolbar {...props} onRefresh={onRefresh} viewMode={viewMode} setViewMode={setViewMode} />,
            event: CustomEvent
          }}
          
          localizer={localizer}
          formats={formats}
          events={events}
          
          startAccessor="start"
          endAccessor="end"
          style={{ height: '100%' }}
          
          eventPropGetter={eventStyleGetter}
          
          selectable={false} // 唯讀
          
          step={30}
          timeslots={2}
          min={minTime}
          max={maxTime}
        />
      </div>
    </div>
  );
};

export default StaffRosterView;